import { t } from 'i18next';
import moment from 'moment';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { handleErrorMessage } from '../../../adapters/primary/libs/handleErrorMessage';
import InMemoryDatasGateway from '../../../adapters/secondary/gateways/InMemoryDatasGateway';
import { selectCurrentParticipantId } from '../../../module/participant/participant.slice';
import { EConsoData } from '../../models/entities/datas/consoData';
import { ELocalData } from '../../models/entities/datas/localData';
import { setError } from '../error/errorActions';
import { selectLatestDateWithData } from '../operation/operationSelector';
import { selectOperationParticipants, selectUserParticipants } from '../participants/participantsSelector';
import { DatasActions } from './datasActions';
import {
  ADD_ONE_TIME_UNIT,
  GET_CONSODATA,
  GET_LATEST_CONSODATA,
  GET_LOCALDATA,
  GET_MEASUREMENT_RANGE,
  GET_OPERATION_CONSODATA,
  GET_OPERATION_MYPART_LOCALPRODUCTION,
  SUBTRACT_ONE_TIME_UNIT,
} from './datasConstants';
import { datasGatewayInterface } from './datasGatewayInterface';
import {
  selectDateFrom,
  selectDateTo,
  selectSelectedDatasCurrentDate,
  selectSelectedDatasInterval,
} from './datasSelector';
import { operationGatewayInterface } from '../operation/operationGatewayInterface';
import { selectCurrentOperation } from '../../../module/operation/operation.slice';

function* getConso(): any {
  try {
    yield put(DatasActions.incrementFetching());
    const participantId = yield select(selectCurrentParticipantId);
    const interval = yield select(selectSelectedDatasInterval);
    const currentDate = yield select(selectSelectedDatasCurrentDate);
    const dateFrom = yield select(selectDateFrom);
    const dateTo = yield select(selectDateTo);
    const datasGatewayInterface: datasGatewayInterface = yield getContext('datasGatewayContext');
    const consoDatas: EConsoData[] = yield call(
      datasGatewayInterface.getConso,
      participantId,
      interval,
      currentDate,
      dateFrom,
      dateTo,
    );
    // handle whendata fetched is not valuable to display
    if (consoDatas.length === 0) {
      const dummyValues: datasGatewayInterface = new InMemoryDatasGateway();
      yield put(DatasActions.setConsoDataIsReal(false));
      yield put(
        DatasActions.setConsoData(dummyValues.getConso(participantId, interval, currentDate, dateFrom, dateTo)),
      );
    } else {
      yield put(DatasActions.setConsoDataIsReal(true));
      yield put(DatasActions.setConsoData(consoDatas));
    }
  } catch (error: any) {
    yield put(
      setError({
        status: error?.response?.status,
        message: '[GET ConsoDatas] : ' + handleErrorMessage(error?.response?.status),
      }),
    );
    console.error(error);
  } finally {
    yield put(DatasActions.decrementFetching());
  }
}

function* getOperationConso(): any {
  try {
    yield put(DatasActions.incrementFetching());
    const operationParticipants = yield select(selectOperationParticipants);
    const datasInterval = yield select(selectSelectedDatasInterval);
    const selectedDate = yield select(selectSelectedDatasCurrentDate);
    const startDate = yield select(selectDateFrom);
    const endDate = yield select(selectDateTo);
    const datasGateway: datasGatewayInterface = yield getContext('datasGatewayContext');

    const chunks = splitArrayIntoChunks(operationParticipants);
    const allData: EConsoData[][] = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const data = yield all(
        chunk.map((p: any) => call(datasGateway.getConso, p._id, datasInterval, selectedDate, startDate, endDate)),
      );
      allData.push(...data);
    }

    const mergedData = aggregateConsoData(allData.flat(), datasInterval);

    const hasValidData = mergedData.some((d) => d.power !== undefined && d.power > 0);

    if (!hasValidData) {
      const dummyData = yield all(
        operationParticipants.map((p: any) =>
          call(new InMemoryDatasGateway().getConso, p._id, datasInterval, selectedDate, startDate, endDate),
        ),
      );

      const aggregatedDummyData = aggregateConsoData(dummyData.flat(), datasInterval);

      yield put(DatasActions.setConsoData(aggregatedDummyData));
      yield put(DatasActions.setConsoDataIsReal(false));
    } else {
      yield put(DatasActions.setConsoData(mergedData));
      yield put(DatasActions.setConsoDataIsReal(true));
    }
  } catch (error: any) {
    yield put(
      setError({
        status: error?.response?.status,
        message: '[GET OperationConso] : ' + handleErrorMessage(error?.response?.status),
      }),
    );
    console.error(error);
  } finally {
    yield put(DatasActions.decrementFetching());
  }
}

function getAggregationKey(dateString: string, interval: string): string {
  const date = new Date(dateString);
  const pad = (num: number) => num.toString().padStart(2, '0');

  switch (interval.toLowerCase()) {
    case 'jour':
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:00`;
    case 'mois':
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    case 'année':
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
    case 'période':
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    default:
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:00`;
  }
}

export function aggregateConsoData(consoData: EConsoData[], interval: string): EConsoData[] {
  const aggregatedData = consoData.reduce((acc: Record<string, EConsoData>, curr: EConsoData) => {
    const aggregationKey = getAggregationKey(curr.date, interval);

    if (!acc[aggregationKey]) {
      acc[aggregationKey] = Object.create(Object.getPrototypeOf(curr));
      Object.assign(acc[aggregationKey], curr);
      acc[aggregationKey].power = 0;
      acc[aggregationKey].powerACC = 0;
    }

    acc[aggregationKey].power += curr.power || 0;
    acc[aggregationKey].powerACC += curr.powerACC || 0;

    return acc;
  }, {});
  return Object.values(aggregatedData).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

function getFallbackCurrentDate(latestDateWithData: string): Date {
  return latestDateWithData ? new Date(latestDateWithData) : new Date();
}

export function getParticipantIdsToCheck(
  operationParticipants: Array<{ _id: number }>,
  participantId?: number,
): number[] {
  if (participantId !== undefined) {
    return [participantId];
  }

  return operationParticipants.map((participant: any) => participant._id);
}

function* getLatestConso(action?: { payload?: { participantId?: number } }): any {
  try {
    yield put(DatasActions.incrementFetching());

    const shouldAggregate = !action?.payload?.participantId;
    const interval = yield select(selectSelectedDatasInterval);
    const operation = yield select(selectCurrentOperation);
    const latestDateWithData: string = yield select(selectLatestDateWithData);
    const operationParticipants = yield select(selectOperationParticipants);
    const participantIdsToCheck = getParticipantIdsToCheck(operationParticipants, action?.payload?.participantId);
    const fetchedParticipantData: Array<{ participantId: number; consoData: EConsoData[] }> = [];
    const datasGatewayInterface: datasGatewayInterface = yield getContext('datasGatewayContext');
    const operationGatewayInterface: operationGatewayInterface = yield getContext('operationGatewayContext');
    let resolvedConsoData: EConsoData[] = [];
    let latestDateForLatestConsoOrSelectedConso = null;

    for (const candidateParticipantId of participantIdsToCheck) {
      const date = yield call(operationGatewayInterface.getLatestDateWithData, operation.id, candidateParticipantId);
      if (!date) continue;
      const consoDatas: EConsoData[] = yield call(
        datasGatewayInterface.getConso,
        candidateParticipantId,
        interval,
        shouldAggregate ? new Date(latestDateWithData) : new Date(date),
      );
      fetchedParticipantData.push({ participantId: candidateParticipantId, consoData: consoDatas });
      if (!latestDateForLatestConsoOrSelectedConso || date > latestDateForLatestConsoOrSelectedConso) {
        latestDateForLatestConsoOrSelectedConso = date;
      }
      if (consoDatas.length !== 0 && resolvedConsoData.length === 0) {
        resolvedConsoData = consoDatas;
      }
    }
    yield put(DatasActions.setCurrentDate(getFallbackCurrentDate(latestDateForLatestConsoOrSelectedConso)));

    if (shouldAggregate) {
      const aggregatedConsoData = aggregateConsoData(
        fetchedParticipantData.flatMap(({ consoData }) => consoData),
        interval,
      );
      if (aggregatedConsoData.length !== 0) {
        yield put(DatasActions.setConsoDataIsReal(true));
        yield put(DatasActions.setConsoData(aggregatedConsoData));
      }
    } else if (resolvedConsoData.length !== 0) {
      yield put(DatasActions.setConsoDataIsReal(true));
      yield put(DatasActions.setConsoData(resolvedConsoData));
    }
  } catch (error: any) {
    console.error(error);
    yield put(
      setError({
        status: error.response.status,
        message: '[GET ConsoDatas] : ' + handleErrorMessage(error.response.status),
      }),
    );
  } finally {
    yield put(DatasActions.decrementFetching());
  }
}

function checkIfLocalProductionValuesAreReal(localDatas: ELocalData[]): boolean {
  // if every values returned are -1, then those values are not displayable
  let checkIfReal: boolean = false;
  localDatas.forEach((localData) => {
    if (localData._value !== -1) checkIfReal = true;
  });
  return checkIfReal;
}

function* getLocalProduction(): any {
  try {
    yield put(DatasActions.incrementFetching());

    const participantId = yield select(selectCurrentParticipantId);
    const interval = yield select(selectSelectedDatasInterval);
    const currentDate = yield select(selectSelectedDatasCurrentDate);
    const dateFrom = yield select(selectDateFrom);
    const dateTo = yield select(selectDateTo);
    const datasGatewayInterface: datasGatewayInterface = yield getContext('datasGatewayContext');
    const localDatas: ELocalData[] = yield call(
      datasGatewayInterface.getLocalProductionByInterval,
      participantId,
      interval,
      currentDate,
      dateFrom,
      dateTo,
    );
    // handle when data fetched is not valuable to display
    if (checkIfLocalProductionValuesAreReal(localDatas)) {
      yield put(DatasActions.setLocalDataIsReal(true));
      yield put(DatasActions.setLocalData([{ participantId, data: localDatas }]));
    } else {
      const dummyValues: datasGatewayInterface = new InMemoryDatasGateway();
      const dummyDatas: ELocalData[] = yield call(
        [dummyValues, dummyValues.getLocalProductionByInterval],
        participantId,
        interval,
        currentDate,
        dateFrom,
        dateTo,
      );
      yield put(DatasActions.setLocalDataIsReal(false));
      yield put(DatasActions.setLocalData([{ participantId, data: dummyDatas }]));
    }
  } catch (error: any) {
    yield put(
      setError({
        status: error?.response?.status,
        message: '[GET LocalProductionDatas] : ' + handleErrorMessage(error?.response?.status),
      }),
    );
    console.error(error);
  } finally {
    yield put(DatasActions.decrementFetching());
  }
}

const splitArrayIntoChunks = (elements: any[]) => {
  const CHUNK_SIZE = 5;
  const chunks = [];
  for (let i = 0; i < elements.length; i += CHUNK_SIZE) {
    chunks.push(elements.slice(i, i + CHUNK_SIZE));
  }
  return chunks;
};

function* getOperationMyPartLocalProduction(): any {
  try {
    yield put(DatasActions.incrementFetching());
    const operationParticipants = yield select(selectOperationParticipants);
    const datasInterval = yield select(selectSelectedDatasInterval);
    const selectedDate = yield select(selectSelectedDatasCurrentDate);
    const startDate = yield select(selectDateFrom);
    const endDate = yield select(selectDateTo);
    const datasGateway: datasGatewayInterface = yield getContext('datasGatewayContext');

    const chunks = splitArrayIntoChunks(operationParticipants);
    const allData: { participantId: number; data: ELocalData[] }[] = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkResults: ELocalData[][] = yield all(
        chunk.map((p: any) =>
          call(datasGateway.getLocalProductionByInterval, p._id, datasInterval, selectedDate, startDate, endDate),
        ),
      );
      chunk.forEach((p: any, idx: number) => {
        allData.push({ participantId: p._id, data: chunkResults[idx] });
      });
    }

    yield put(DatasActions.setLocalData(allData));
    yield put(DatasActions.setLocalDataIsReal(true));
  } catch (error: any) {
    yield put(
      setError({
        status: error?.response?.status,
        message: '[GET OperationLocalProduction] : ' + handleErrorMessage(error?.response?.status),
      }),
    );
  } finally {
    yield put(DatasActions.decrementFetching());
  }
}

function* addOneTimeUnit() {
  function currentDatePlusOneTimeUnit(dateToAdd: Date, interval: string): Date {
    const newDate = dateToAdd;
    switch (interval) {
      case t('words.day'):
        newDate.setDate(newDate.getDate() + 1);
        break;
      case t('words.month'):
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      case t('words.year'):
        newDate.setMonth(newDate.getMonth() + 12);
        break;
      default:
        break;
    }
    return newDate;
  }

  try {
    const interval: string = yield select(selectSelectedDatasInterval);
    const currentDate: Date = yield select(selectSelectedDatasCurrentDate);
    const newDate = currentDatePlusOneTimeUnit(currentDate, interval);
    yield put(DatasActions.setCurrentDate(new Date(newDate)));
  } catch (error: any) {
    yield put(
      setError({
        status: error.response.status,
        message: '[addOneTimeUnitSaga] : ' + handleErrorMessage(error.response.status),
      }),
    );
    console.error(error);
  }
}

function* subtractOneTimeUnit() {
  function currentDateMinusOneTimeUnit(dateToAdd: Date, interval: string): Date {
    const newDate = dateToAdd;

    switch (interval) {
      case t('words.day'):
        newDate.setDate(newDate.getDate() - 1);
        break;
      case t('words.month'):
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      case t('words.year'):
        newDate.setMonth(newDate.getMonth() - 12);
        break;
      default:
        break;
    }
    return newDate;
  }

  try {
    const interval: string = yield select(selectSelectedDatasInterval);
    const currentDate: Date = yield select(selectSelectedDatasCurrentDate);
    const newDate = currentDateMinusOneTimeUnit(currentDate, interval);
    yield put(DatasActions.setCurrentDate(new Date(newDate)));
  } catch (error: any) {
    yield put(
      setError({
        status: error.response.status,
        message: '[addOneTimeUnitSaga] : ' + handleErrorMessage(error.response.status),
      }),
    );
    console.error(error);
  }
}

function* getMeasurementRange(action: any) {
  const consolidateRanges = (dates: any) => {
    const consolidatedRange =
      dates.length > 0
        ? dates.reduce((acc: any, curr: any) => ({
            from: acc.from < curr.from ? acc.from : curr.from,
            to: acc.to > curr.to ? acc.to : curr.to,
          }))
        : null;
    const range = {
      from: consolidatedRange && moment(consolidatedRange.from),
      to: consolidatedRange && moment(consolidatedRange.to),
    };

    return range;
  };

  try {
    let participantId: number = yield select(selectCurrentParticipantId);
    const participants: any[] = yield select(selectUserParticipants);
    const { PRM } = action.payload;
    if (PRM) {
      participantId = participants.find((participant) => participant._dsoDetails._prm === PRM)._id;
    }
    const datasGatewayInterface: datasGatewayInterface = yield getContext('datasGatewayContext');
    let uploadedDatesRange: any[] = [];
    if (participants.length) {
      for (const participant of participants) {
        const dateRange: any[] = yield call(datasGatewayInterface.getDateRanges, participant._id);
        uploadedDatesRange.push(...dateRange);
      }
    } else {
      uploadedDatesRange = yield call(datasGatewayInterface.getDateRanges, participantId);
    }
    yield put(
      DatasActions.setUploadedDateRange(
        consolidateRanges(uploadedDatesRange).from,
        consolidateRanges(uploadedDatesRange).to,
      ),
    );
  } catch (error: any) {
    yield put(
      setError({
        status: error?.response?.status,
        message: '[getMeasurementRange] : ' + handleErrorMessage(error?.response?.status),
      }),
    );
    console.error(error);
  }
}

export function* subtractOneTimeUnitSaga() {
  yield takeLatest(SUBTRACT_ONE_TIME_UNIT, subtractOneTimeUnit);
}

export function* getMeasurementRangeSaga() {
  yield takeLatest(GET_MEASUREMENT_RANGE, getMeasurementRange);
}

export function* addOneTimeUnitSaga() {
  yield takeLatest(ADD_ONE_TIME_UNIT, addOneTimeUnit);
}

export function* getConsoDataSaga() {
  yield takeLatest(GET_CONSODATA, getConso);
}

export function* getLatestConsoDataSaga() {
  yield takeLatest(GET_LATEST_CONSODATA, getLatestConso);
}

export function* getLocalDataSaga() {
  yield takeLatest(GET_LOCALDATA, getLocalProduction);
}

export function* getOperationConsoDataSaga() {
  yield takeLatest(GET_OPERATION_CONSODATA, getOperationConso);
}

export function* getOperationLocalDataSaga() {
  yield takeLatest(GET_OPERATION_MYPART_LOCALPRODUCTION, getOperationMyPartLocalProduction);
}

export default [
  getLocalDataSaga,
  addOneTimeUnitSaga,
  subtractOneTimeUnitSaga,
  getConsoDataSaga,
  getLatestConsoDataSaga,
  getMeasurementRangeSaga,
  getOperationConsoDataSaga,
  getOperationLocalDataSaga,
];
